import React from 'react';
import { Empty } from 'antd';
import { StyledTable } from '../../../entity/shared/components/styled/StyledTable';
import { ExecutionRequest } from '../../../../types.generated';
import { ButtonsColumn, StatusColumn, TimeColumn } from './IngestionExecutionTableColumns';
import { getDurationFromTime } from '../../../../utils/formatter';
import { useTranslation } from '../../../../i18n/useTranslation';
import { SUCCESS } from '../utils';

interface Props {
    executionRequests: ExecutionRequest[];
    setFocusExecutionUrn: (urn: string) => void;
    handleViewDetails: (urn: string) => void;
    handleCancelExecution: (urn: string) => void;
    handleRollbackExecution: (runId: string) => void;
    isLoading: boolean;
}

export default function IngestionExecutionTable({
    executionRequests,
    setFocusExecutionUrn,
    handleViewDetails,
    handleCancelExecution,
    handleRollbackExecution,
    isLoading,
}: Props) {
    const { t } = useTranslation();
    const tableColumns = [
        {
            width: '45%',
            title: t('Requested At'),
            dataIndex: 'requestedAt',
            className: 'f-white-space-nowrap',
            key: 'requestedAt',
            render: TimeColumn,
        },
        {
            width: '13%',
            title: t('Started At'),
            dataIndex: 'executedAt',
            className: 'f-white-space-nowrap',
            key: 'executedAt',
            render: TimeColumn,
        },
        {
            width: '10%',
            title: t('Duration (s)'),
            className: 'f-white-space-nowrap',
            dataIndex: 'duration',
            key: 'duration',
            render: (durationMs: number) => {
                if (!durationMs) return t('None');

                return getDurationFromTime('ms')(durationMs);
            },
        },
        {
            width: '15%',
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: any, record) => (
                <StatusColumn t={t} status={status} record={record} setFocusExecutionUrn={setFocusExecutionUrn} />
            ),
        },
        // Commenting this as per the requirement of PRIV-211629,
        // which is to hide the column for now
        // {
        //     width: '20%',
        //     title: t('Execution'),
        //     dataIndex: 'source',
        //     key: 'source',
        //     render: SourceColumn,
        // },
        {
            title: '',
            dataIndex: '',
            key: 'x',
            render: (_, record: any) => (
                <ButtonsColumn
                    t={t}
                    record={record}
                    isLoading={isLoading}
                    handleViewDetails={handleViewDetails}
                    handleCancelExecution={handleCancelExecution}
                    handleRollbackExecution={handleRollbackExecution}
                />
            ),
        },
    ];

    const mostRecentSuccessfulExecution = executionRequests.find((execution) => execution.result?.status === SUCCESS);

    const tableData = executionRequests.map((execution) => ({
        urn: execution.urn,
        id: execution.id,
        source: execution.input.source.type,
        requestedAt: execution.input?.requestedAt,
        executedAt: execution.result?.startTimeMs,
        duration: execution.result?.durationMs,
        status: execution.result?.status,
        showRollback: execution.urn === mostRecentSuccessfulExecution?.urn,
    }));

    return (
        <StyledTable
            columns={tableColumns}
            dataSource={tableData}
            rowKey="id"
            locale={{
                emptyText: <Empty description={t('No Executions found!')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
            pagination={false}
        />
    );
}
